// Money + quantity formatting helpers.
// Centralised so the whole app speaks USD consistently and we can
// localise once when we go beyond the first restaurant.

const MONEY = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

export const formatMoney = (n) => MONEY.format(Number(n) || 0)

// e.g. 1.5 -> "1.5", 2 -> "2", and tacks on the unit if provided.
export function formatQty(n, unit) {
  const num = Number(n)
  if (!isFinite(num)) return unit ? `0 ${unit}` : '0'
  // Integer if whole, otherwise up to 2 decimals trimmed.
  const str =
    Number.isInteger(num) ? String(num) : Number(num.toFixed(2)).toString()
  return unit ? `${str} ${unit}` : str
}

export function formatDelta(n) {
  const v = Number(n) || 0
  const sign = v > 0 ? '+' : v < 0 ? '−' : ''
  return `${sign}${formatMoney(Math.abs(v))}`
}

// Null/negative-safe relative-time humanizer ("just now", "5 min ago",
// "2 hr ago", "3 days ago"). Returns '' for null/undefined/unparseable
// input and for future timestamps (ms < 0), so callers can skip rendering
// rather than show "NaN"/"Invalid Date". Centralised here so badges and
// cards never duplicate timestamp math.
export function timeAgo(iso) {
  if (!iso) return ''
  const ms = Date.now() - new Date(iso).getTime()
  if (!Number.isFinite(ms) || ms < 0) return ''
  const minutes = Math.floor(ms / 60_000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes} min ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} hr ago`
  const days = Math.floor(hours / 24)
  return `${days} day${days === 1 ? '' : 's'} ago`
}

// Humanize a time-saved figure (whole minutes) into a sentence-case
// "X hours" / "X hr Y min" string for the time-saved ledger headline.
//   30  -> "30 min"
//   60  -> "1 hour"
//   90  -> "1 hr 30 min"
//   600 -> "10 hours"
// Never renders "0 minutes" / "0 hours" — the card's empty state handles
// the zero case (eventCount === 0), so a 0/negative/non-finite input
// returns '' and the caller skips the headline rather than show a bare
// zero. Whole-hour values use the long word ("hour"/"hours"); mixed
// values use the compact "hr"/"min" form so the headline stays tight.
export function formatHoursSaved(minutes) {
  const total = Math.round(Number(minutes))
  if (!Number.isFinite(total) || total <= 0) return ''
  const hours = Math.floor(total / 60)
  const mins = total % 60
  if (hours === 0) {
    return `${mins} min`
  }
  if (mins === 0) {
    return `${hours} hour${hours === 1 ? '' : 's'}`
  }
  return `${hours} hr ${mins} min`
}

// Format a stored event time ("HH:MM" or "HH:MM:SS", 24-hour) as a friendly
// 12-hour clock: "12:30:00" -> "12:30 PM", "09:00" -> "9:00 AM", "00:15" ->
// "12:15 AM". Returns '' for empty/unparseable input so callers can skip it
// (never render the raw "12:30:00" machine value on a customer surface).
export function formatEventTime(t) {
  if (!t || typeof t !== 'string') return ''
  const m = t.match(/^(\d{1,2}):(\d{2})/)
  if (!m) return ''
  let h = Number(m[1])
  const min = Number(m[2])
  if (!Number.isFinite(h) || !Number.isFinite(min) || h > 23 || min > 59) return ''
  const period = h >= 12 ? 'PM' : 'AM'
  h = h % 12
  if (h === 0) h = 12
  return `${h}:${String(min).padStart(2, '0')} ${period}`
}
